import React from 'react'
import { MapPin, Calendar, Users, Repeat } from 'lucide-react'

export default function EventCard({ event, onClick }) {
  const {
    title,
    imageUrl,
    locationName,
    recurrenceRule,
    upcomingOccurrences,
  } = event

  const nextOccurrence = upcomingOccurrences?.[0]

  const formatDate = (dateString) => {
    if (!dateString) return null
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // The public event API leaves RSVP counts out on purpose; show the count
  // only when the data has it, rather than a misleading "0 RSVPs".
  const hasRsvpCounts = (upcomingOccurrences || []).some(
    (occ) => typeof occ.rsvpCount === 'number',
  )
  const totalRsvps = (upcomingOccurrences || []).reduce(
    (sum, occ) => sum + (occ.rsvpCount || 0),
    0,
  )

  return (
    <div
      className="bg-white border border-surface-border transition-all duration-200 h-full flex flex-col relative overflow-hidden cursor-pointer hover:border-gold/30 hover:shadow-subtle"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }}
    >
      {/* Cover Image */}
      {imageUrl && (
        <div className="relative w-full h-48 flex-shrink-0 overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

          {recurrenceRule && (
            <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-gold/15 backdrop-blur-sm px-2 py-0.5 rounded-sm">
              <Repeat className="w-3.5 h-3.5 text-gold" />
              <span className="text-xs text-gold font-semibold">Recurring</span>
            </div>
          )}
        </div>
      )}

      {/* No-image recurring badge */}
      {!imageUrl && recurrenceRule && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-gold/15 px-2 py-0.5 rounded-sm">
          <Repeat className="w-3.5 h-3.5 text-gold" />
          <span className="text-xs text-gold font-semibold">Recurring</span>
        </div>
      )}

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col min-h-0">
        <h3 className="text-xl font-bold font-display text-charcoal mb-2 line-clamp-2">
          {title}
        </h3>

        {nextOccurrence && (
          <div className="flex items-center gap-2 text-charcoal-muted mb-2">
            <Calendar className="w-4 h-4 flex-shrink-0 text-charcoal-subtle" />
            <span className="text-sm">{formatDate(nextOccurrence.startTime)}</span>
          </div>
        )}

        {locationName && (
          <div className="flex items-center gap-2 text-charcoal-muted mb-3">
            <MapPin className="w-4 h-4 flex-shrink-0 text-charcoal-subtle" />
            <span className="text-sm line-clamp-1">{locationName}</span>
          </div>
        )}

        {hasRsvpCounts && (
          <div className="mt-auto pt-3 border-t border-surface-border flex items-center gap-2 text-charcoal-muted">
            <Users className="w-4 h-4 text-charcoal-subtle" />
            <span className="text-sm">
              {totalRsvps} {totalRsvps === 1 ? 'RSVP' : 'RSVPs'}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
